import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { learningApi } from "../api/learning";
import type { CreateCoursePayload } from "../api/learning";
import type {
  CreateQuizQuestionPayload,
  SubmitQuizPayload,
  SaveRoleLearningDefaults,
} from "../api/generated/openapi";
import { requireData } from "./http";

const keys = {
  all: ["learning"] as const,
  courses: ["learning", "courses"] as const,
  detail: (id: string) => ["learning", "course", id] as const,
};
export const useCourses = () =>
  useQuery({
    queryKey: keys.courses,
    queryFn: () => learningApi.listCourses().then(requireData),
  });
export const useCourse = (id: string) =>
  useQuery({
    queryKey: keys.detail(id),
    queryFn: () => learningApi.getCourse(id).then(requireData),
    enabled: Boolean(id),
  });
const useInvalidatingMutation = <T>(fn: (value: T) => Promise<unknown>) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  });
};
export const useCreateCourse = () =>
  useInvalidatingMutation((payload: CreateCoursePayload) =>
    learningApi.createCourse(payload).then(requireData),
  );
export const useAddQuestion = () =>
  useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: CreateQuizQuestionPayload }) =>
      learningApi.addQuestion(id, payload).then(requireData),
  );
export const usePublishCourse = () =>
  useInvalidatingMutation((id: string) =>
    learningApi.publish(id).then(requireData),
  );
export const useAssignCourse = () =>
  useInvalidatingMutation(
    ({
      id,
      userId,
      required,
    }: {
      id: string;
      userId: string;
      required?: boolean;
    }) =>
      learningApi.assign(id, { user_id: userId, required }).then(requireData),
  );
export const useSubmitQuiz = () =>
  useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: SubmitQuizPayload }) =>
      learningApi.submit(id, payload).then(requireData),
  );
export const useCompleteCourse = () =>
  useInvalidatingMutation((id: string) =>
    learningApi.complete(id).then(requireData),
  );

export const useAssignedCourses = (userId: string) =>
  useQuery({
    queryKey: ["learning", "assigned", userId],
    queryFn: () => learningApi.listCourses(userId).then(requireData),
    enabled: Boolean(userId),
  });

export const useOnboardingStatus = (userId?: string) =>
  useQuery({
    queryKey: ["learning", "onboarding", userId],
    queryFn: () => learningApi.onboarding().then(requireData),
    enabled: Boolean(userId),
    refetchInterval: 30000,
  });
export const useLearningAssignments = (userId: string) =>
  useQuery({
    queryKey: ["learning", "assignments", userId],
    queryFn: () => learningApi.assignments(userId).then(requireData),
    enabled: Boolean(userId),
  });
export const useRoleLearningDefaults = () =>
  useQuery({
    queryKey: ["learning", "role-defaults"],
    queryFn: () => learningApi.roleDefaults().then(requireData),
  });
export const useSaveRoleLearningDefaults = () =>
  useInvalidatingMutation((payload: SaveRoleLearningDefaults) =>
    learningApi.saveRoleDefaults(payload).then(requireData),
  );

export const useUpdateCourse = () =>
  useInvalidatingMutation(
    ({ id, payload }: { id: string; payload: CreateCoursePayload }) =>
      learningApi.updateCourse(id, payload).then(requireData),
  );
